/**
 * PHASE 1: Offline Data Layer
 * IndexedDB wrapper for Chain29 offline sync engine
 * Database: asterpos_offline
 */

const DB_NAME = 'asterpos_offline';
const DB_VERSION = 2;

const STORES = {
  SYNC_QUEUE: 'sync_queue',
  SYNC_LOG: 'sync_log',
  LOCAL_CACHE_META: 'local_cache_meta'
};

/**
 * Queue Item Shape
 * @typedef {Object} QueueItem
 * @property {string} id - UUID
 * @property {string} type - TIMECLOCK_PUNCH | AVAILABILITY_UPDATE | SCHEDULE_ACK | GENERIC_API_ACTION
 * @property {string} endpoint - /api/timeclock/v2/punch
 * @property {string} method - POST | PUT | PATCH | DELETE
 * @property {Object} payload
 * @property {Object} headers
 * @property {number} createdAt
 * @property {number} updatedAt
 * @property {'pending'|'syncing'|'completed'|'failed'|'conflict'} status
 * @property {number} retryCount
 * @property {number} nextRetryAt
 * @property {string|null} lastError
 * @property {string} idempotencyKey
 * @property {string|null} entityType - timeclock, availability, schedule
 * @property {string|null} entityId
 * @property {string|null} userId
 * @property {string|null} merchantId
 * @property {number} clientGeneratedAt
 * @property {number} priority - lower = higher priority
 * @property {boolean} requiresAuth
 * @property {number} version - optimistic/local schema version
 */

let dbInstance = null;

/**
 * Generate UUID v4
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Initialize offline database
 */
export async function initOfflineDb() {
  if (dbInstance) return dbInstance;
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = () => {
      dbInstance = request.result;
      console.log('[OfflineDB] Database opened successfully');
      resolve(dbInstance);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      console.log('[OfflineDB] Upgrading database schema...');
      
      // SYNC_QUEUE store
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const queueStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
        queueStore.createIndex('status', 'status', { unique: false });
        queueStore.createIndex('nextRetryAt', 'nextRetryAt', { unique: false });
        queueStore.createIndex('type', 'type', { unique: false });
        queueStore.createIndex('createdAt', 'createdAt', { unique: false });
        queueStore.createIndex('merchantId', 'merchantId', { unique: false });
        queueStore.createIndex('userId', 'userId', { unique: false });
        queueStore.createIndex('idempotencyKey', 'idempotencyKey', { unique: false });
        queueStore.createIndex('entityType_entityId', ['entityType', 'entityId'], { unique: false });
        queueStore.createIndex('priority_createdAt', ['priority', 'createdAt'], { unique: false });
      }
      
      // SYNC_LOG store
      if (!db.objectStoreNames.contains(STORES.SYNC_LOG)) {
        const logStore = db.createObjectStore(STORES.SYNC_LOG, { keyPath: 'id', autoIncrement: true });
        logStore.createIndex('queueItemId', 'queueItemId', { unique: false });
        logStore.createIndex('syncedAt', 'syncedAt', { unique: false });
        logStore.createIndex('result', 'result', { unique: false });
      }
      
      // LOCAL_CACHE_META store
      if (!db.objectStoreNames.contains(STORES.LOCAL_CACHE_META)) {
        const cacheStore = db.createObjectStore(STORES.LOCAL_CACHE_META, { keyPath: 'key' });
        cacheStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

/**
 * Get database instance
 */
async function getDb() {
  if (!dbInstance) {
    await initOfflineDb();
  }
  return dbInstance;
}

// ==================== QUEUE OPERATIONS ====================

/**
 * Add item to sync queue
 * @param {Partial<QueueItem>} item
 * @returns {Promise<QueueItem>}
 */
export async function addQueueItem(item) {
  const db = await getDb();
  const now = Date.now();
  
  const queueItem = {
    id: item.id || generateUUID(),
    type: item.type || 'GENERIC_API_ACTION',
    endpoint: item.endpoint,
    method: item.method || 'POST',
    payload: item.payload || {},
    headers: item.headers || {},
    createdAt: now,
    updatedAt: now,
    status: 'pending',
    retryCount: 0,
    nextRetryAt: now,
    lastError: null,
    idempotencyKey: item.idempotencyKey || generateUUID(),
    entityType: item.entityType || null,
    entityId: item.entityId || null,
    userId: item.userId || null,
    merchantId: item.merchantId || null,
    clientGeneratedAt: item.clientGeneratedAt || now,
    priority: item.priority ?? 5,
    requiresAuth: item.requiresAuth ?? true,
    version: item.version || 1
  };
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const request = store.add(queueItem);
    
    request.onsuccess = () => {
      console.log('[OfflineDB] Queue item added:', queueItem.id);
      resolve(queueItem);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get pending queue items sorted by priority and createdAt
 * @param {number} limit
 * @returns {Promise<QueueItem[]>}
 */
export async function getPendingQueueItems(limit = 50) {
  const db = await getDb();
  const now = Date.now();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const index = store.index('status');
    const request = index.getAll('pending');
    
    request.onsuccess = () => {
      let items = request.result || [];
      // Filter by nextRetryAt and sort by priority, then createdAt
      items = items
        .filter(item => item.nextRetryAt <= now)
        .sort((a, b) => {
          if (a.priority !== b.priority) return a.priority - b.priority;
          return a.createdAt - b.createdAt;
        })
        .slice(0, limit);
      resolve(items);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get queue item by ID
 * @param {string} id
 * @returns {Promise<QueueItem|null>}
 */
export async function getQueueItemById(id) {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update queue item
 * @param {string} id
 * @param {Partial<QueueItem>} patch
 * @returns {Promise<QueueItem>}
 */
export async function updateQueueItem(id, patch) {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const getRequest = store.get(id);
    
    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (!item) {
        reject(new Error(`Queue item not found: ${id}`));
        return;
      }
      
      const updated = {
        ...item,
        ...patch,
        updatedAt: Date.now()
      };
      
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(putRequest.error);
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
}

/**
 * Delete queue item
 * @param {string} id
 */
export async function deleteQueueItem(id) {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const request = store.delete(id);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * List queue items with filters
 * @param {Object} filters - { status?, type?, userId?, merchantId? }
 * @returns {Promise<QueueItem[]>}
 */
export async function listQueueItems(filters = {}) {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    
    let request;
    if (filters.status) {
      const index = store.index('status');
      request = index.getAll(filters.status);
    } else if (filters.type) {
      const index = store.index('type');
      request = index.getAll(filters.type);
    } else {
      request = store.getAll();
    }
    
    request.onsuccess = () => {
      let items = request.result || [];
      
      // Apply additional filters
      if (filters.userId) {
        items = items.filter(i => i.userId === filters.userId);
      }
      if (filters.merchantId) {
        items = items.filter(i => i.merchantId === filters.merchantId);
      }
      if (filters.entityType) {
        items = items.filter(i => i.entityType === filters.entityType);
      }
      
      resolve(items);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get queue item by idempotency key
 * @param {string} idempotencyKey
 * @returns {Promise<QueueItem|null>}
 */
export async function getQueueItemByIdempotencyKey(idempotencyKey) {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const index = store.index('idempotencyKey');
    const request = index.get(idempotencyKey);
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Find existing queue item for entity (for deduplication)
 * @param {string} entityType
 * @param {string} entityId
 * @returns {Promise<QueueItem|null>}
 */
export async function findPendingByEntity(entityType, entityId) {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const index = store.index('entityType_entityId');
    const request = index.getAll([entityType, entityId]);
    
    request.onsuccess = () => {
      const pending = (request.result || []).find(i => i.status === 'pending');
      resolve(pending || null);
    };
    request.onerror = () => reject(request.error);
  });
}

// ==================== SYNC LOG OPERATIONS ====================

/**
 * Add sync log entry
 * @param {Object} entry
 */
export async function addSyncLog(entry) {
  const db = await getDb();
  
  const logEntry = {
    ...entry,
    syncedAt: entry.syncedAt || Date.now()
  };
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_LOG, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_LOG);
    const request = store.add(logEntry);
    
    request.onsuccess = () => resolve(logEntry);
    request.onerror = () => reject(request.error);
  });
}

/**
 * List sync log entries
 * @param {number} limit
 * @returns {Promise<Object[]>}
 */
export async function listSyncLog(limit = 100) {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_LOG, 'readonly');
    const store = tx.objectStore(STORES.SYNC_LOG);
    const index = store.index('syncedAt');
    const request = index.openCursor(null, 'prev');
    
    const results = [];
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor && results.length < limit) {
        results.push(cursor.value);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

// ==================== CLEANUP OPERATIONS ====================

/**
 * Clear completed queue items older than specified time
 * @param {number} olderThanMs - milliseconds
 */
export async function clearCompletedQueueItems(olderThanMs = 7 * 24 * 60 * 60 * 1000) {
  const db = await getDb();
  const cutoff = Date.now() - olderThanMs;
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const index = store.index('status');
    const request = index.openCursor('completed');
    
    let deletedCount = 0;
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.updatedAt < cutoff) {
          cursor.delete();
          deletedCount++;
        }
        cursor.continue();
      } else {
        console.log(`[OfflineDB] Cleared ${deletedCount} completed items`);
        resolve(deletedCount);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get queue statistics
 */
export async function getQueueStats() {
  const db = await getDb();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
    const store = tx.objectStore(STORES.SYNC_QUEUE);
    const request = store.getAll();
    
    request.onsuccess = () => {
      const items = request.result || [];
      const stats = {
        total: items.length,
        pending: items.filter(i => i.status === 'pending').length,
        syncing: items.filter(i => i.status === 'syncing').length,
        completed: items.filter(i => i.status === 'completed').length,
        failed: items.filter(i => i.status === 'failed').length,
        conflict: items.filter(i => i.status === 'conflict').length
      };
      resolve(stats);
    };
    request.onerror = () => reject(request.error);
  });
}

// Export store names for external use
export { STORES, generateUUID };

export default {
  initOfflineDb,
  addQueueItem,
  getPendingQueueItems,
  getQueueItemById,
  updateQueueItem,
  deleteQueueItem,
  listQueueItems,
  getQueueItemByIdempotencyKey,
  findPendingByEntity,
  addSyncLog,
  listSyncLog,
  clearCompletedQueueItems,
  getQueueStats,
  generateUUID
};
